import { Box, Card, CardContent, Skeleton, Tooltip, Typography } from '@mui/material';
import ReactApexChart from 'react-apexcharts';
import { applyTrendTooltip } from '../../system/apex-tooltip';
import { reportColors, styleReportChart } from './report-design';
import { applyApexBrandGradient } from '../../system/apex-gradient';

const formatNumber = (value) => new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Number(value || 0));
const formatPercent = (value) => `${Number(value || 0).toFixed(2)}%`;
const truncateText = (value, max = 36) => {
    const text = String(value || '');
    if (text.length <= max) return text;
    return `${text.slice(0, max - 3)}...`;
};

const ensureApexOptions = (options, chartType, theme, reportStyle) => {
    const base = options && typeof options === 'object' ? options : {};
    const merged = applyApexBrandGradient({
        ...base,
        chart: {
            zoom: { enabled: false },
            toolbar: { show: false },
            ...(base.chart || {}),
            ...(chartType ? { type: chartType } : {})
        }
    }, theme, chartType);
    return applyTrendTooltip(theme, reportStyle ? styleReportChart(theme, { ...merged, colors: reportColors.categories }) : merged);
};

export const getMetaEngagementColumns = ({ entityLabel = 'Ad Set', totals = {} }) => ([
    {
        Header: () => (
            <Tooltip title={<div><Typography fontWeight={900}>{entityLabel}</Typography><Typography>{`${entityLabel} engagement performance.`}</Typography></div>}>
                <span>{entityLabel}</span>
            </Tooltip>
        ),
        header: entityLabel,
        accessorKey: 'label',
        grow: 1,
        size: 300,
        Cell: ({ cell }) => (
            <Typography textAlign="left" fontSize={13} fontWeight={900}>
                {cell.row.original.label}
            </Typography>
        ),
        Footer: () => (
            <Typography fontSize={13} fontWeight={900} textAlign="left">
                Totals
            </Typography>
        )
    },
    {
        header: 'Engagement',
        accessorKey: 'engagementTotal',
        Cell: ({ cell }) => <Typography fontSize={13} fontWeight={700}>{formatNumber(cell.row.original.engagementTotal)}</Typography>,
        Footer: () => <Typography fontSize={13} fontWeight={900}>{formatNumber(totals.engagementTotal)}</Typography>
    },
    {
        header: 'Reactions',
        accessorKey: 'reactions',
        Cell: ({ cell }) => <Typography fontSize={13} fontWeight={700}>{formatNumber(cell.row.original.reactions)}</Typography>,
        Footer: () => <Typography fontSize={13} fontWeight={900}>{formatNumber(totals.reactions)}</Typography>
    },
    {
        header: 'Comments',
        accessorKey: 'comments',
        Cell: ({ cell }) => <Typography fontSize={13} fontWeight={700}>{formatNumber(cell.row.original.comments)}</Typography>,
        Footer: () => <Typography fontSize={13} fontWeight={900}>{formatNumber(totals.comments)}</Typography>
    },
    {
        header: 'Shares',
        accessorKey: 'shares',
        Cell: ({ cell }) => <Typography fontSize={13} fontWeight={700}>{formatNumber(cell.row.original.shares)}</Typography>,
        Footer: () => <Typography fontSize={13} fontWeight={900}>{formatNumber(totals.shares)}</Typography>
    },
    {
        header: 'Saves',
        accessorKey: 'saves',
        Cell: ({ cell }) => <Typography fontSize={13} fontWeight={700}>{formatNumber(cell.row.original.saves)}</Typography>,
        Footer: () => <Typography fontSize={13} fontWeight={900}>{formatNumber(totals.saves)}</Typography>
    }
]);

const MetaEngagementSection = ({
    loading,
    reportStyle = false,
    rows,
    totals,
    theme,
    cardSx,
    table,
    emptyMessage = 'No engagement metrics were returned for this date range.',
    entityLabel = 'Ad Set'
}) => {
    const safeRows = Array.isArray(rows) ? rows : [];
    const entityPlural = entityLabel.endsWith('s') ? entityLabel : `${entityLabel}s`;
    const entitySignalLabel = entityLabel.toLowerCase();
    const safeTotals = {
        impressions: Number(totals?.impressions || 0),
        clicks: Number(totals?.clicks || 0),
        engagementTotal: Number(totals?.engagementTotal || 0),
        reactions: Number(totals?.reactions || 0),
        comments: Number(totals?.comments || 0),
        shares: Number(totals?.shares || 0),
        saves: Number(totals?.saves || 0)
    };

    const engagementRate = safeTotals.impressions > 0 ? (safeTotals.engagementTotal / safeTotals.impressions) * 100 : 0;
    const reactionShare = safeTotals.engagementTotal > 0 ? (safeTotals.reactions / safeTotals.engagementTotal) * 100 : 0;
    const commentShare = safeTotals.engagementTotal > 0 ? (safeTotals.comments / safeTotals.engagementTotal) * 100 : 0;
    const saveShare = safeTotals.engagementTotal > 0 ? (safeTotals.saves / safeTotals.engagementTotal) * 100 : 0;
    const amplificationShare = safeTotals.engagementTotal > 0 ? ((safeTotals.shares + safeTotals.saves) / safeTotals.engagementTotal) * 100 : 0;
    const clickEngagementShare = safeTotals.clicks > 0 ? (safeTotals.engagementTotal / safeTotals.clicks) * 100 : 0;

    const strongestRow = safeRows.slice().sort((a, b) => (b.engagementTotal || 0) - (a.engagementTotal || 0))[0] || null;
    const topRows = safeRows.slice().sort((a, b) => (b.engagementTotal || 0) - (a.engagementTotal || 0)).slice(0, 8);
    const breakdownRows = safeRows.slice().sort((a, b) => (b.engagementTotal || 0) - (a.engagementTotal || 0)).slice(0, 6);
    const showSourceBreakdown = breakdownRows.length > 1;

    const signalEntries = [
        { label: 'Reactions', value: safeTotals.reactions },
        { label: 'Comments', value: safeTotals.comments },
        { label: 'Shares', value: safeTotals.shares },
        { label: 'Saves', value: safeTotals.saves }
    ];
    const primarySignal = signalEntries.reduce((best, entry) => (entry.value > best.value ? entry : best), signalEntries[0] || { label: 'Reactions', value: 0 });

    const mixOptions = {
        labels: ['Reactions', 'Comments', 'Shares', 'Saves'],
        chart: { type: 'donut' },
        legend: { position: 'bottom' },
        dataLabels: { enabled: false },
        colors: [
            theme.palette.primary.main,
            theme.palette.secondary.main,
            theme.palette.primary.light,
            theme.palette.secondary.light
        ],
        plotOptions: {
            pie: {
                donut: {
                    size: '60%',
                    labels: {
                        show: true,
                        total: {
                            show: true,
                            label: 'Total',
                            formatter: () => formatNumber(safeTotals.engagementTotal)
                        }
                    }
                }
            }
        },
        tooltip: { y: { formatter: (val) => formatNumber(val) } }
    };

    const profileOptions = {
        chart: { type: 'bar' },
        plotOptions: { bar: { horizontal: true, barHeight: '58%', borderRadius: 6 } },
        dataLabels: { enabled: false },
        colors: [theme.palette.primary.main],
        xaxis: {
            categories: ['Engagement Rate', 'Reaction Share', 'Comment Share', 'Amplification', 'Click to Engage'],
            labels: { formatter: (val) => `${Number(val).toFixed(1)}%` }
        },
        tooltip: { y: { formatter: (val) => formatPercent(val) } }
    };
    const profileSeries = [{
        name: 'Share',
        data: [engagementRate, reactionShare, commentShare, amplificationShare, clickEngagementShare]
    }];

    const topSourcesOptions = {
        chart: { type: 'bar' },
        plotOptions: { bar: { horizontal: true, barHeight: '55%', borderRadius: 6 } },
        dataLabels: { enabled: false },
        colors: [theme.palette.primary.main],
        xaxis: {
            categories: topRows.map((row) => truncateText(row.label, 34)),
            labels: { formatter: (val) => formatNumber(val) }
        },
        tooltip: { y: { formatter: (val) => formatNumber(val) } }
    };
    const topSourcesSeries = [{ name: 'Engagement', data: topRows.map((row) => row.engagementTotal || 0) }];

    const breakdownOptions = {
        chart: { type: 'bar', stacked: true },
        plotOptions: { bar: { horizontal: true, barHeight: '55%', borderRadius: 6 } },
        dataLabels: { enabled: false },
        colors: [
            theme.palette.primary.main,
            theme.palette.secondary.main,
            theme.palette.primary.light,
            theme.palette.secondary.light
        ],
        xaxis: {
            categories: breakdownRows.map((row) => truncateText(row.label, 32)),
            labels: { formatter: (val) => formatNumber(val) }
        },
        legend: { position: 'top' },
        tooltip: { y: { formatter: (val) => formatNumber(val) } }
    };
    const breakdownSeries = [
        { name: 'Reactions', data: breakdownRows.map((row) => row.reactions || 0) },
        { name: 'Comments', data: breakdownRows.map((row) => row.comments || 0) },
        { name: 'Shares', data: breakdownRows.map((row) => row.shares || 0) },
        { name: 'Saves', data: breakdownRows.map((row) => row.saves || 0) }
    ];

    const insightCards = [
        {
            title: 'Primary Signal',
            value: primarySignal.label,
            note: `${formatNumber(primarySignal.value)} interactions, ${formatPercent(safeTotals.engagementTotal > 0 ? (primarySignal.value / safeTotals.engagementTotal) * 100 : 0)} of total engagement`
        },
        {
            title: `Leading ${entityLabel}`,
            value: strongestRow?.label || `No ${entitySignalLabel} signal`,
            note: strongestRow
                ? `${formatNumber(strongestRow.engagementTotal)} total engagements in this range`
                : `No ${entitySignalLabel}-level engagement rows were returned.`
        },
        {
            title: 'Interaction Profile',
            value: `${formatPercent(commentShare)} comments`,
            note: `${formatPercent(amplificationShare)} shares + saves, ${formatPercent(saveShare)} saves only`
        }
    ];

    if (loading) {
        return (
            <Box sx={{ display: 'grid', gap: 2 }}>
                <Skeleton variant="rectangular" height={220} sx={cardSx} />
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(6, minmax(0, 1fr))' }, gap: 1 }}>
                    {Array.from({ length: 6 }).map((_, index) => (
                        <Skeleton key={`engagement-kpi-${index}`} variant="rectangular" height={90} sx={cardSx} />
                    ))}
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2 }}>
                    <Skeleton variant="rectangular" height={280} sx={cardSx} />
                    <Skeleton variant="rectangular" height={280} sx={cardSx} />
                </Box>
            </Box>
        );
    }

    if (!safeTotals.engagementTotal && safeRows.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary" align="center">
                {emptyMessage}
            </Typography>
        );
    }

    return (
        <Box sx={{ display: 'grid', gap: 2 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(6, minmax(0, 1fr))' }, gap: 1 }}>
                {[
                    { label: 'Total Engagement', value: formatNumber(safeTotals.engagementTotal) },
                    { label: 'Reactions', value: formatNumber(safeTotals.reactions) },
                    { label: 'Comments', value: formatNumber(safeTotals.comments) },
                    { label: 'Shares', value: formatNumber(safeTotals.shares) },
                    { label: 'Saves', value: formatNumber(safeTotals.saves) },
                    { label: 'Engagement Rate', value: formatPercent(engagementRate) }
                ].map((item) => (
                    <Card key={item.label} variant="outlined">
                        <CardContent>
                            <Typography variant="caption" color="text.secondary">{item.label}</Typography>
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>{item.value}</Typography>
                        </CardContent>
                    </Card>
                ))}
            </Box>

            {table}

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, gap: 1 }}>
                {insightCards.map((item) => (
                    <Card key={item.title} variant="outlined">
                        <CardContent>
                            <Typography variant="caption" color="text.secondary">{item.title}</Typography>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 0.5 }}>
                                {item.value}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4 }}>
                                {item.note}
                            </Typography>
                        </CardContent>
                    </Card>
                ))}
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1.05fr 0.95fr' }, gap: 2 }}>
                <Card variant="outlined">
                    <CardContent>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                            Engagement Mix
                        </Typography>
                        {safeTotals.engagementTotal > 0 ? (
                            <ReactApexChart
                                options={ensureApexOptions(mixOptions, 'donut', theme, reportStyle)}
                                series={[safeTotals.reactions, safeTotals.comments, safeTotals.shares, safeTotals.saves]}
                                type="donut"
                                height={300}
                            />
                        ) : (
                            <Skeleton variant="rectangular" height={240} sx={cardSx} />
                        )}
                    </CardContent>
                </Card>
                <Card variant="outlined">
                    <CardContent>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                            Engagement Profile
                        </Typography>
                        <ReactApexChart
                            options={ensureApexOptions(profileOptions, 'bar', theme, reportStyle)}
                            series={profileSeries}
                            type="bar"
                            height={300}
                        />
                    </CardContent>
                </Card>
            </Box>

            {showSourceBreakdown ? (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2 }}>
                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                                {`Top ${entityPlural} by Engagement`}
                            </Typography>
                            <ReactApexChart
                                options={ensureApexOptions(topSourcesOptions, 'bar', theme, reportStyle)}
                                series={topSourcesSeries}
                                type="bar"
                                height={320}
                            />
                        </CardContent>
                    </Card>
                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                                {`Engagement Composition by ${entityLabel}`}
                            </Typography>
                            <ReactApexChart
                                options={ensureApexOptions(breakdownOptions, 'bar', theme, reportStyle)}
                                series={breakdownSeries}
                                type="bar"
                                height={320}
                            />
                        </CardContent>
                    </Card>
                </Box>
            ) : null}
        </Box>
    );
};

export default MetaEngagementSection;
